/**
 * Flickcall — Netflix-party watch-together with video call
 *
 * A single WAR entry exposes `img/*`, `css/*`, `icons/*`, `webfonts/*`,
 * `js/players/*` and `html/*` to every origin with no `use_dynamic_url` — so its
 * icon set and the per-service party stylesheets are all fetchable from any page at a
 * fixed URL.
 *
 * The bundled icon is the cleanest signal: an image loads through `probeImage` with no
 * CORS grant, so it survives a restrictive page CSP.
 *
 * Manifest verified against version 11.8.7.
 */

import { probeImage, probeFetch } from '../core/probe';
import type { ExtensionDefinition } from '../core/types';

const ID = 'bclkldcgifcioppjcncibkjdfaghldoe';

export const flickcall: ExtensionDefinition = {
  id: ID,
  name: 'Flickcall: Netflix Party with Video Call',
  storeUrl: `https://chromewebstore.google.com/detail/${ID}`,
  users: 100_000,
  notes:
    'Exposes its icons and per-service party stylesheets to every origin with no dynamic ' +
    'URL. The icon loads without a CORS grant; detection is conclusive.',
  signals: [
    {
      id: 'war-icon-48',
      kind: 'web-accessible-resource',
      description:
        'Load icons/48X48.png via <img> — a bundled icon exposed to every origin; needs ' +
        'no CORS grant, so it works even under a strict page CSP.',
      weight: 1,
      run: () => probeImage(ID, 'icons/48X48.png'),
    },
    {
      id: 'war-patreon-logo',
      kind: 'web-accessible-resource',
      description:
        'Load img/patreonlogo.png via <img> — a second bundled image, corroborating the ' +
        'identification.',
      weight: 0.5,
      run: () => probeImage(ID, 'img/patreonlogo.png'),
    },
    {
      id: 'war-party-hulu-css',
      kind: 'web-accessible-resource',
      description:
        'Fetch css/content/party_hulu.css — one of the per-service party stylesheets, a ' +
        'path independent of the images.',
      weight: 0.5,
      run: () => probeFetch(ID, 'css/content/party_hulu.css'),
    },
  ],
};
